import {
  LEARNING_MODALITIES,
  type LearningMaterial,
  type LearningModality,
} from '@common/domain/learning-workspace';

/**
 * Per-participant learning-modality ordering, sourced from the Cognito
 * `custom:module_order` claim (surfaced as `UserInfo.moduleOrder`).
 *
 * Participants always progress through modules 1, 2, 3 in that fixed order —
 * a module is a chapter, identified by its manifest `order`. What's randomized
 * per participant is *which modality* (textbook/chat/avatar) they use for each
 * module: entry `i` of the parsed claim is the 1-based modality number
 * (1=textbook, 2=chat, 3=avatar, per `LEARNING_MODALITIES`) assigned to module
 * `i + 1`. E.g. "3,1,2" means module 1 is done via avatar, module 2 via
 * textbook, module 3 via chat.
 *
 * Two halves:
 *   - `parseModuleOrder` turns the raw claim into a trusted `number[]` (or
 *     `undefined` for anything malformed).
 *   - `resolveModuleAssignments` turns that array (plus the manifest) into an
 *     ordered list of module -> modality -> chapter assignments, always in
 *     module order (module 1 first) — this is both the sidebar's tab order
 *     and the source of each modality's default chapter within
 *     `LearningWorkspacePage`.
 *
 * Both fail closed the same way: a missing modality order, or one that isn't a
 * genuine permutation of 1..N (wrong length, out-of-range entry, or a repeated
 * entry — e.g. "2,3,4"), is rejected as a whole and the identity assignment is
 * used for every module. Neither function ever accepts a partially-valid
 * order and patches just the bad entries — that per-position patching is what
 * used to let a malformed claim assign two different modules the same
 * modality, which the sidebar (keyed by modality) would then silently
 * collapse into one tab.
 */
function isValidModalityOrder(order: readonly number[], moduleCount: number): boolean {
  return (
    order.length === moduleCount &&
    order.every((n) => Number.isInteger(n) && n >= 1 && n <= moduleCount) &&
    new Set(order).size === order.length
  );
}

export function parseModuleOrder(raw: string | null | undefined): readonly number[] | undefined {
  if (!raw) return undefined;
  const parsed = raw.split(',').map((part) => Number(part.trim()));
  return isValidModalityOrder(parsed, LEARNING_MODALITIES.length) ? parsed : undefined;
}

export interface ModuleAssignment {
  modality: LearningModality;
  moduleNumber: number;
  chapterSlug: string;
  title: string;
}

/** Identity fallback: module N uses modality N (1=textbook, 2=chat, 3=avatar). */
const DEFAULT_MODALITY_ORDER: readonly number[] = [1, 2, 3];

/**
 * Resolves the fixed module-1/2/3 progression to a modality and chapter for
 * each module, honoring a per-participant modality order. Always returns
 * assignments in module order (module 1 first) — that order is also the
 * sidebar's display order, since a participant always does module 1 before
 * module 2 before module 3, regardless of which modality each one uses.
 *
 * A module with no resolvable *enabled* material (e.g. a chapter was
 * disabled) is dropped from the result rather than throwing, so the
 * remaining modules still render.
 */
export function resolveModuleAssignments(
  materials: readonly LearningMaterial[],
  modalityOrder?: readonly number[],
): ModuleAssignment[] {
  const enabledMaterials = materials.filter((material) => material.enabled);
  const moduleCount = LEARNING_MODALITIES.length;
  const effectiveOrder =
    modalityOrder && isValidModalityOrder(modalityOrder, moduleCount)
      ? modalityOrder
      : DEFAULT_MODALITY_ORDER;

  const assignments: ModuleAssignment[] = [];
  for (let index = 0; index < moduleCount; index += 1) {
    const moduleNumber = index + 1;
    const material = enabledMaterials.find((candidate) => candidate.order === moduleNumber);
    if (!material) continue;

    const modality = LEARNING_MODALITIES[effectiveOrder[index] - 1];
    assignments.push({ modality, moduleNumber, chapterSlug: material.slug, title: material.title });
  }
  return assignments;
}
